import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Gift, Copy, Check, Share2, Zap } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const ReferralProgram = ({ user = null, subscription = null }) => {
    const [copied, setCopied] = useState(false);
    const [referralLink, setReferralLink] = useState('');
    const [stats, setStats] = useState({ referred: 0, rewardsEarned: 0 });

    // Generate referral code locally as fallback
    const localCode = user?.uid ? `CT-${user.uid.substring(0, 6).toUpperCase()}` : 'CT-XXXXXX';
    const localLink = `https://rastorai.com/?ref=${localCode}`;

    useEffect(() => {
        if (!user) return;

        const init = async () => {
            // Create / fetch referral link
            try {
                const result = await featuresAPI.createReferralLink(user.displayName);
                if (result?.referral_link) {
                    setReferralLink(result.referral_link);
                } else {
                    setReferralLink(localLink);
                }
            } catch {
                setReferralLink(localLink);
            }

            // Fetch referral stats
            try {
                const statsResult = await featuresAPI.getReferralStats();
                if (statsResult) {
                    setStats({
                        referred: statsResult.total_referrals || statsResult.referred || 0,
                        rewardsEarned: statsResult.rewards_earned || (statsResult.total_referrals || 0) * 7,
                    });
                }
            } catch {
                // Use local fallback
                const localCount = parseInt(localStorage.getItem('ct_referral_count') || '0');
                setStats({ referred: localCount, rewardsEarned: localCount * 7 });
            }
        };

        init();
    }, [user, localLink]);

    const displayLink = referralLink || localLink;

    const handleCopy = () => {
        navigator.clipboard.writeText(displayLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareOnWhatsApp = () => {
        const text = `Hey! I've been using Rastor AI to plan my career switch and it's amazing. Use my referral link to get 7 days of Premium free: ${displayLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareOnTwitter = () => {
        const text = `I'm using @Rastor AI to accelerate my career growth! 🚀 Get 7 days of Premium free with my referral link:`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(displayLink)}`, '_blank');
    };

    if (!user) return null;

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-purple-500/10 via-violet-500/5 to-pink-500/10 p-6 border-b border-white/5">
                <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-5 h-5 text-purple-400" />
                    <h3 className="font-bold text-white text-sm">Refer & Earn</h3>
                </div>
                <p className="text-xs text-slate-400">
                    Invite friends and both of you get <span className="text-purple-400 font-bold">7 days of Premium free</span>!
                </p>
            </div>

            <div className="p-6 space-y-4">
                {/* Referral Code */}
                <div>
                    <label className="text-[10px] font-mono text-slate-500 uppercase mb-1 block">Your Referral Link</label>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#0B0F19] border border-white/10 rounded-lg px-3 py-2.5 text-xs font-mono text-slate-300 truncate">
                            {displayLink}
                        </div>
                        <button
                            onClick={handleCopy}
                            className="px-3 py-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 hover:bg-purple-500/20 transition-colors"
                        >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                {/* Share Buttons */}
                <div className="flex gap-2">
                    <button
                        onClick={shareOnWhatsApp}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#25D366]/10 border border-[#25D366]/20 rounded-lg text-[#25D366] text-xs font-medium hover:bg-[#25D366]/20 transition-colors"
                    >
                        <span>WhatsApp</span>
                    </button>
                    <button
                        onClick={shareOnTwitter}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#1DA1F2]/10 border border-[#1DA1F2]/20 rounded-lg text-[#1DA1F2] text-xs font-medium hover:bg-[#1DA1F2]/20 transition-colors"
                    >
                        <span>Twitter</span>
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="bg-purple-500/5 border border-purple-500/10 rounded-xl p-3 text-center">
                        <div className="text-xl font-bold text-purple-400">{stats.referred}</div>
                        <div className="text-[10px] text-slate-500 font-mono uppercase">Friends Referred</div>
                    </div>
                    <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-3 text-center">
                        <div className="text-xl font-bold text-amber-400">{stats.rewardsEarned}</div>
                        <div className="text-[10px] text-slate-500 font-mono uppercase">Days Earned</div>
                    </div>
                </div>

                {/* How it works */}
                <div className="border-t border-white/5 pt-4">
                    <h4 className="text-[10px] font-mono text-slate-500 uppercase mb-3">How It Works</h4>
                    <div className="space-y-2">
                        {[
                            { step: '1', text: 'Share your unique referral link' },
                            { step: '2', text: 'Friend signs up using your link' },
                            { step: '3', text: 'Both of you get 7 days Premium free!' },
                        ].map((item) => (
                            <div key={item.step} className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="w-5 h-5 bg-purple-500/10 text-purple-400 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">
                                    {item.step}
                                </span>
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferralProgram;
